import type { ImageVariable } from '@carboncopyco/types';
import { Button, TextButton } from '@carboncopyco/ui';
import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import type { InferType } from 'yup';
import { ImageDropzone } from '../../ImageDropzone';
import type { ImageInputProps } from '../../ImageInput';
import { ImageInput } from '../../ImageInput';
import { InputField } from '../../InputField';
import { useImageUrlDetails } from '../../hooks';
import { VariableFormInput } from '../VariableFormInput';
import type { VariableFormBaseProps } from '../types';
import { useFormValidation } from '../useFormValidation';
import { useFormSchema } from './useFormSchema';

type FormFields = Pick<
  ImageVariable,
  'name' | 'value' | 'description' | 'imageMetadata'
>;

export interface ImageVariableFormProps extends VariableFormBaseProps {
  variable?: ImageVariable;
}

const initialValues: FormFields = {
  name: '',
  value: '',
  description: '',
  imageMetadata: undefined,
};

const getInitialValues = (variable: ImageVariable): FormFields => {
  if (variable) {
    return {
      name: variable.name,
      value: variable.value,
      description: variable.description,
      imageMetadata: variable.imageMetadata,
    };
  }

  return initialValues;
};

export const ImageVariableForm = ({
  autoFocus = false,
  onFocus,
  onCancel,
  onSubmit,
  variable,
  variables = [],
}: ImageVariableFormProps) => {
  const [formValues, setFormValues] = useState<FormFields>(
    getInitialValues(variable)
  );
  const inputRef = useRef<HTMLInputElement>();
  const schema = useFormSchema({ variable, variables });
  const [errors, setErrors] = useState<InferType<typeof schema>>(initialValues);
  const validateForm = useFormValidation(schema, setErrors);

  const [imageDetailsCalculated, status] = useImageUrlDetails(
    variable
      ? {
          url: variable.value,
          metadata: variable.imageMetadata,
        }
      : undefined
  );
  const [uploadStatus, setUploadStatus] =
    useState<ImageInputProps['state']>('idle');

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const onSubmitHandler = async () => {
    const isFormValid = await validateForm(formValues);
    if (!isFormValid) return;
    const newVariable: Omit<ImageVariable, 'variableId'> = {
      type: 'image',
      value: formValues.value,
      imageMetadata: formValues.imageMetadata,
      name: formValues.name.trim(),
      description: formValues.description.trim(),
    };

    try {
      await onSubmit(newVariable);
    } catch (error) {
      console.error(error);
    }
  };

  const isEditing = !!variable;
  const wasEdited = isEqual(variable, {
    ...variable,
    ...formValues,
  });
  const isHavingFormErrors = Object.values(errors).some((error) => error);
  const isMissingMandatoryFields = !formValues.name || !formValues.value;

  return (
    <div className="flex flex-col gap-4 relative">
      <div className="flex flex-col gap-3.5">
        <VariableFormInput
          label="Name"
          name="name"
          value={formValues.name}
          ref={inputRef}
          autoFocus={autoFocus}
          onFocus={onFocus}
          onChange={(e) => {
            setFormValues((prev) => ({
              ...prev,
              name: e.target.value,
            }));
            setErrors((prev) => ({ ...prev, name: '' }));
          }}
          error={errors.name}
        />
        <VariableFormInput
          label="Description"
          name="description"
          value={formValues.description}
          onChange={(e) => {
            setFormValues((prev) => ({
              ...prev,
              description: e.target.value,
            }));
            setErrors((prev) => ({
              ...prev,
              description: '',
            }));
          }}
          error={errors.description}
        />
        <InputField
          label="Default value"
          name="defaultValue"
          error={
            (uploadStatus === 'error' &&
              'Image files must be in jpg, jpeg, or png format, and not exceed 10MB.') ||
            (status === 'error' && 'Image type is not supported.')
          }
        >
          <ImageDropzone
            onLoading={() => {
              setFormValues((prev) => ({
                ...prev,
                value: '',
                metadata: undefined,
              }));
              setUploadStatus('loading');
            }}
            onError={() => {
              setFormValues((prev) => ({
                ...prev,
                value: '',
                metadata: undefined,
              }));
              setUploadStatus('error');
            }}
            onSuccess={(image) => {
              setFormValues((prev) => ({
                ...prev,
                value: image.url,
                imageMetadata: image.metadata,
              }));
              setUploadStatus('idle');
            }}
          >
            <ImageInput
              value={
                formValues.value
                  ? {
                      url: formValues.value,
                      metadata: formValues.imageMetadata,
                    }
                  : imageDetailsCalculated
              }
              state={status}
              hoverable
              placeholder={<PlaceholderText />}
              metadata={<PlaceholderText />}
            />
          </ImageDropzone>
        </InputField>
      </div>
      <div className="flex flex-row gap-2.5 justify-end">
        <Button
          label="Cancel"
          onClick={onCancel}
          highContrast
        />
        <Button
          label={variable ? 'Save' : 'Add'}
          onClick={onSubmitHandler}
          disabled={
            (isEditing && wasEdited) ||
            isHavingFormErrors ||
            isMissingMandatoryFields
          }
        />
      </div>
    </div>
  );
};

const PlaceholderText = () => (
  <TextButton
    label="Upload image"
    color="text-ds-primary-300"
  />
);
